import { ICheckpointRule, ITripGrouping } from '../../interfaces';

/**
 * Structural layer of the trips importer templating system: turns the mapped rows of a
 * customer file (one row per order, say) into the canonical rows the importer pipeline
 * understands (one row per checkpoint/order, grouped by tripCode + checkpointIndex).
 *
 * It only consumes the cell layer's output (`MappedRow.values`), so any parser feeds it.
 * Pure: trip codes for the `autogenerate` strategy are assigned afterwards by the caller.
 */

/** A source row after its cell mappings ran: canonical fields and `$variables`, as text. */
export interface MappedRow {
    /** 1-based row number in the user's file, kept so errors point at what the user sees. */
    rowNumber: number;
    values: Record<string, string>;
}

export interface ReshapedTrip {
    /** Joined tripKey values. */
    key: string;
    /** Canonical rows. Each one carries the trip-level fields of the trip's first source row. */
    rows: MappedRow[];
}

export interface ReshapeError {
    rowNumber: number;
    reason: 'missing_trip_key' | 'missing_trip_code' | 'missing_location' | 'conflicting_location';
    field: string;
    given?: string;
    expected?: string;
}

/** Fields that belong to an order: they only travel on rows of the order-carrying checkpoint. */
const ORDER_FIELDS = [
    'orderCode',
    'orderType',
    'productCode',
    'productDescription',
    'productQuantity',
    'productWeight',
    'pallets',
];

export function reshapeRows(
    rows: MappedRow[],
    grouping: ITripGrouping,
    checkpoints?: ICheckpointRule[],
): { trips: ReshapedTrip[]; errors: ReshapeError[] } {
    const errors: ReshapeError[] = [];
    const groups = groupRows(rows, grouping, errors);

    const trips = [...groups.entries()].map(([key, tripRows]) => {
        if (grouping.tripCode.strategy === 'field') {
            const tripCode = tripRows[0].values['tripCode'];
            if (!tripCode)
                errors.push({ rowNumber: tripRows[0].rowNumber, reason: 'missing_trip_code', field: 'tripCode' });
            // Rows of one group must land in one trip downstream, which groups by tripCode.
            tripRows = tripRows.map((row) => withValues(row, { tripCode }));
        }
        return { key, rows: checkpoints?.length ? planCheckpoints(tripRows, checkpoints, errors) : tripRows };
    });

    return { trips, errors };
}

function groupRows(rows: MappedRow[], grouping: ITripGrouping, errors: ReshapeError[]): Map<string, MappedRow[]> {
    // Map keeps insertion order: trips come out in order of first appearance in the file.
    const groups = new Map<string, MappedRow[]>();
    rows.forEach((row) => {
        const keyValues = grouping.tripKey.map((field) => row.values[field] ?? '');
        if (keyValues.every((value) => !value)) {
            errors.push({ rowNumber: row.rowNumber, reason: 'missing_trip_key', field: grouping.tripKey.join(', ') });
            return;
        }
        const key = keyValues.join('|');
        groups.set(key, [...(groups.get(key) ?? []), row]);
    });
    return groups;
}

/**
 * Emits the canonical rows of one trip following the checkpoint plan. Every rule takes
 * the next checkpoint index(es); skipped optional rules don't consume one.
 */
function planCheckpoints(tripRows: MappedRow[], rules: ICheckpointRule[], errors: ReshapeError[]): MappedRow[] {
    const first = tripRows[0];
    const tripLevel = withoutOrderFields(first);
    const output: MappedRow[] = [];
    let index = 0;

    const checkpointFields = (rule: ICheckpointRule, code: string, checkpointIndex = index) => ({
        checkpointIndex: String(checkpointIndex),
        checkpointCode: code,
        checkpointType: rule.type,
    });
    // Trip-level fields always come from the first row, so every canonical row of the trip agrees.
    const orderRow = (row: MappedRow, checkpoint: Record<string, string>): MappedRow => ({
        rowNumber: row.rowNumber,
        values: { ...tripLevel.values, ...orderFields(row), ...checkpoint },
    });

    for (const rule of rules) {
        // Validated templates always name the field when the location isn't fixed.
        const field = rule.location.field ?? '';

        if ((rule.per ?? 'trip') === 'trip') {
            const code = rule.location.fixed ?? tripLocation(tripRows, field, errors);
            if (code === undefined) continue; // conflict, already reported
            if (!code) {
                if (!rule.optional) errors.push({ rowNumber: first.rowNumber, reason: 'missing_location', field });
                continue;
            }
            index++;
            output.push(
                ...(rule.carriesOrders
                    ? tripRows.map((row) => orderRow(row, checkpointFields(rule, code)))
                    : [withValues(tripLevel, checkpointFields(rule, code))]),
            );
            continue;
        }

        // per: distinct — one checkpoint per location, indexed in order of first appearance.
        const indexByCode = new Map<string, number>();
        for (const row of tripRows) {
            const code = row.values[field];
            if (!code) {
                if (!rule.optional) errors.push({ rowNumber: row.rowNumber, reason: 'missing_location', field });
                continue;
            }
            if (!indexByCode.has(code)) {
                indexByCode.set(code, ++index);
                if (!rule.carriesOrders) output.push(withValues(tripLevel, checkpointFields(rule, code)));
            }
            if (rule.carriesOrders) output.push(orderRow(row, checkpointFields(rule, code, indexByCode.get(code))));
        }
    }

    return output;
}

/**
 * The single location every row of the trip agrees on: '' when none has one,
 * undefined (and an error) when two rows disagree.
 */
function tripLocation(tripRows: MappedRow[], field: string, errors: ReshapeError[]): string | undefined {
    let found: MappedRow | undefined;
    for (const row of tripRows) {
        const code = row.values[field];
        if (!code) continue;
        if (!found) {
            found = row;
        } else if (found.values[field] !== code) {
            errors.push({
                rowNumber: row.rowNumber,
                reason: 'conflicting_location',
                field,
                given: code,
                expected: found.values[field],
            });
            return undefined;
        }
    }
    return found?.values[field] ?? '';
}

function withValues(row: MappedRow, values: Record<string, string>): MappedRow {
    return { rowNumber: row.rowNumber, values: { ...row.values, ...values } };
}

function withoutOrderFields(row: MappedRow): MappedRow {
    const values = Object.fromEntries(Object.entries(row.values).filter(([key]) => !ORDER_FIELDS.includes(key)));
    return { rowNumber: row.rowNumber, values };
}

function orderFields(row: MappedRow): Record<string, string> {
    return Object.fromEntries(Object.entries(row.values).filter(([key]) => ORDER_FIELDS.includes(key)));
}
